import { useEffect, useState } from "react";
import { FileText, FilePlus, ExternalLink, Trash2, Share2, Plus, Loader2 } from "lucide-react";
import api, { fmtNaira } from "@/lib/api";
import { toast } from "sonner";
import { shareToWhatsApp } from "@/lib/share";

const BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Documents page: list quotes/invoices, create new, download PDF/PNG, share to WhatsApp.
 */
export default function Documents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | quote | invoice
  const [showCreate, setShowCreate] = useState(null); // "quote" | "invoice" | null

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/documents/list${filter !== "all" ? `?kind=${filter}` : ""}`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter]);

  async function shareDoc(doc) {
    try {
      const send = await api.post(`/documents/${doc.id}/send`);
      const r = await shareToWhatsApp({ text: send.data.text, phone: doc.customer_phone || null, title: `${doc.kind === "quote" ? "Quotation" : "Invoice"} · ${doc.number}` });
      if (r.via === "clipboard") toast.success("Text copied — paste in WhatsApp 📋");
      load();
    } catch (e) {
      toast.error("Couldn't share");
    }
  }

  return (
    <div className="p-4 space-y-4" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Quotes & Invoices</h1>
          <p className="text-xs text-kudi-ink/55 mt-0.5">Create, share and track sales documents.</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", "quote", "invoice"].map((k) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === k ? "bg-kudi-green text-white" : "bg-accent/40 text-kudi-ink/70 hover:bg-accent"}`}
            data-testid={`filter-${k}`}>
            {k === "all" ? "All" : k === "quote" ? "Quotes" : "Invoices"}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowCreate("quote")}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-kudi-green text-kudi-green hover:bg-kudi-green hover:text-white transition"
            data-testid="new-quote-btn">
            <Plus className="w-3 h-3 inline mr-1" /> Quotation
          </button>
          <button onClick={() => setShowCreate("invoice")}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-kudi-terracotta text-white hover:bg-kudi-terracotta-dark transition"
            data-testid="new-invoice-btn">
            <FilePlus className="w-3 h-3 inline mr-1" /> Invoice
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-kudi-ink/50 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-10 text-center" data-testid="documents-empty">
          <FileText className="w-8 h-8 text-kudi-ink/30 mx-auto mb-2" />
          <div className="font-heading font-semibold">No {filter === "all" ? "documents" : filter + "s"} yet</div>
          <div className="text-xs text-kudi-ink/50 mt-1">Send your first professional-looking quotation in under a minute.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="bg-white border border-border rounded-2xl p-3.5 flex items-center gap-3" data-testid={`doc-${d.id}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${d.kind === "quote" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                <FileText className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-heading font-semibold text-sm">{d.number}</span>
                  <span className="text-[10px] uppercase tracking-wider bg-accent/40 rounded-full px-1.5 py-0.5">{d.kind}</span>
                  <span className={`text-[10px] uppercase font-bold rounded-full px-1.5 py-0.5 ${d.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{d.status}</span>
                </div>
                <div className="text-xs text-kudi-ink/60 truncate">{d.customer_name} · {d.items?.length || 0} items</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-heading font-bold text-sm tabular-nums">{fmtNaira(d.total)}</div>
                <div className="flex items-center gap-1 mt-1">
                  <a href={`${BASE}/api/documents/${d.id}.pdf`} target="_blank" rel="noopener noreferrer" className="text-kudi-ink/50 hover:text-kudi-ink" title="Download PDF" data-testid={`doc-pdf-${d.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button onClick={() => shareDoc(d)} className="text-kudi-green hover:text-kudi-green-dark" title="Share on WhatsApp" data-testid={`doc-share-${d.id}`}>
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateDocModal kind={showCreate} onClose={() => setShowCreate(null)} onCreated={() => { setShowCreate(null); load(); }} />
      )}
    </div>
  );
}

function CreateDocModal({ kind, onClose, onCreated }) {
  const [customer, setCustomer] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [items, setItems] = useState([{ name: "", qty: 1, unit_price: 0 }]);
  const [notes, setNotes] = useState("");
  const [dueDays, setDueDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);

  useEffect(() => {
    api.get("/inventory").then((r) => setInventoryItems(r.data.items || r.data || [])).catch(() => {});
  }, []);

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);

  function updateItem(i, patch) {
    setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  }
  function pickInventory(i, invItem) {
    updateItem(i, { name: invItem.name, unit_price: invItem.sell_price || invItem.cost_price || 0 });
  }

  async function submit(e) {
    e.preventDefault();
    if (!customer.trim()) return toast.error("Enter customer name");
    if (!items.length || items.some((it) => !it.name.trim() || Number(it.qty) <= 0 || Number(it.unit_price) < 0)) return toast.error("Fill in all line items");
    setSaving(true);
    try {
      const body = {
        customer_name: customer.trim(),
        customer_phone: customerPhone || undefined,
        items: items.map((it) => ({ name: it.name.trim(), qty: Number(it.qty), unit_price: Number(it.unit_price) })),
        notes: notes || undefined,
        due_days: Number(dueDays) || 7,
      };
      await api.post(`/documents/${kind}`, body);
      toast.success(`${kind === "quote" ? "Quotation" : "Invoice"} created`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="create-doc-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 shadow-2xl my-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-bold">New {kind === "quote" ? "Quotation" : "Invoice"}</h3>
          <button type="button" onClick={onClose} className="text-kudi-ink/50 hover:text-kudi-ink" data-testid="doc-close">✕</button>
        </div>
        <input type="text" required value={customer} onChange={(e) => setCustomer(e.target.value)}
          placeholder="Customer name *" className="w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-green focus:outline-none" data-testid="doc-customer" />
        <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)}
          placeholder="Customer WhatsApp (optional)" className="w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-green focus:outline-none" data-testid="doc-customer-phone" />

        <div className="space-y-2">
          <div className="text-xs font-semibold text-kudi-ink/70 uppercase">Line items</div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5" data-testid={`doc-item-${i}`}>
              <div className="col-span-6">
                <input list={`inv-${i}`} value={it.name} onChange={(e) => {
                  const inv = inventoryItems.find((x) => x.name?.toLowerCase() === e.target.value.toLowerCase());
                  if (inv) pickInventory(i, inv); else updateItem(i, { name: e.target.value });
                }} placeholder="Item" className="w-full border border-border rounded-lg px-2 py-2 text-sm focus:border-kudi-green focus:outline-none" />
                <datalist id={`inv-${i}`}>
                  {inventoryItems.map((inv) => <option key={inv.id} value={inv.name}>{inv.name} · {fmtNaira(inv.sell_price || 0)}</option>)}
                </datalist>
              </div>
              <input type="number" min="1" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })}
                placeholder="Qty" className="col-span-2 border border-border rounded-lg px-2 py-2 text-sm focus:border-kudi-green focus:outline-none" />
              <input type="number" min="0" step="1" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                placeholder="Price ₦" className="col-span-3 border border-border rounded-lg px-2 py-2 text-sm focus:border-kudi-green focus:outline-none" />
              <button type="button" onClick={() => setItems(items.filter((_, x) => x !== i))} disabled={items.length <= 1}
                className="col-span-1 text-rose-500 hover:text-rose-700 disabled:opacity-30" data-testid={`doc-item-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => {
              setItems([...items, { name: "", qty: 1, unit_price: 0 }]);
              try { if (typeof window.fbq === "function") window.fbq("track", "AddToCart", { content_type: "document_line_item", content_category: "quotation_or_invoice" }); } catch { /* noop */ }
            }}
            className="text-xs text-kudi-green hover:underline" data-testid="doc-add-item">+ Add item</button>
        </div>

        {kind === "invoice" && (
          <label className="block">
            <span className="text-xs font-semibold text-kudi-ink/70">Due in (days)</span>
            <input type="number" min="1" max="90" value={dueDays} onChange={(e) => setDueDays(e.target.value)}
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-green focus:outline-none" data-testid="doc-due-days" />
          </label>
        )}

        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200}
          placeholder="Notes (optional)" className="w-full border border-border rounded-lg px-3 py-2 focus:border-kudi-green focus:outline-none text-sm" data-testid="doc-notes" />

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-sm text-kudi-ink/60">Total</span>
          <span className="font-heading text-2xl font-bold text-kudi-green tabular-nums" data-testid="doc-total">{fmtNaira(subtotal)}</span>
        </div>

        <button type="submit" disabled={saving}
          className="w-full bg-kudi-green text-white font-semibold rounded-xl py-3 disabled:opacity-50 transition hover:bg-kudi-green-dark" data-testid="doc-save">
          {saving ? "Creating…" : `Create ${kind === "quote" ? "Quotation" : "Invoice"}`}
        </button>
      </form>
    </div>
  );
}
